import React, { useState, useEffect, useMemo, useRef } from "react";
import { useBikeStore } from "../../store/useBikeStore";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";
import { useCityStore } from "../../store/useCityStore";
import type { Bike, BikeWithLocation } from "../../types/bike";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { BikeFilters } from "./BikeFilters";
import { BikeTable } from "./BikeTable";
import { BikePagination } from "./BikePagination";
import { BikeModal } from "./BikeModal";
import { BikeCsvImportModal } from "./BikeCsvImportModal";
import { Plus, RefreshCw, Download, Upload } from "lucide-react";
import {
  NO_MAIN_CITY_FILTER_VALUE,
  UNCATEGORIZED_FILTER_VALUE,
} from "../../utils/constants";
import { bikesToCsv, getVehicleCsvFilename } from "../../utils/vehicleCsv";
import { downloadAsCsv } from "../../utils/helpers";

const DEFAULT_ITEMS_PER_PAGE = 10;

export const BikesList: React.FC = () => {
  const {
    bikes,
    loading,
    isRefreshing,
    addBike,
    updateBike,
    removeBike,
    loadIfNeeded,
    refreshBikes,
  } = useBikeStore();
  const {
    categories,
    loadIfNeeded: loadCategoriesIfNeeded,
    refreshCategories,
  } = useVehicleCategoryStore();
  const {
    cities,
    loadIfNeeded: loadCitiesIfNeeded,
    refreshCities,
  } = useCityStore();

  const itemsPerPage = DEFAULT_ITEMS_PER_PAGE;

  // Local state for filters, pagination, and UI
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [sortBy, setSortBy] = useState<string>("bike_id");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showIncludeHidden, setShowIncludeHidden] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "delete">(
    "create",
  );
  const [selectedBike, setSelectedBike] = useState<BikeWithLocation | null>(
    null,
  );
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Debounce timer ref
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce search input
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms delay

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, categoryFilter, cityFilter]);

  useEffect(() => {
    loadIfNeeded().catch(console.error);
    loadCategoriesIfNeeded().catch(console.error);
    loadCitiesIfNeeded().catch(console.error);
  }, [loadIfNeeded, loadCategoriesIfNeeded, loadCitiesIfNeeded]);

  const filteredBikes = useMemo(() => {
    const searchQuery = debouncedSearch.trim().toLowerCase();
    const filtered = bikes.filter((bike) => {
      if (searchQuery) {
        const searchable =
          `${bike.bike_id} ${bike.bike_sn ?? ""}`.toLowerCase();
        if (!searchable.includes(searchQuery)) {
          return false;
        }
      }
      if (statusFilter && bike.status !== statusFilter) {
        return false;
      }
      if (categoryFilter === UNCATEGORIZED_FILTER_VALUE) {
        if (bike.category_id) {
          return false;
        }
      } else if (categoryFilter && bike.category_id !== categoryFilter) {
        return false;
      }
      if (cityFilter === NO_MAIN_CITY_FILTER_VALUE) {
        if (bike.primary_city_id) {
          return false;
        }
      } else if (cityFilter && bike.primary_city_id !== cityFilter) {
        return false;
      }
      return true;
    });

    filtered.sort((a, b) => {
      const getSortValue = (bike: BikeWithLocation): string | number => {
        switch (sortBy) {
          case "last_seen":
            return bike.last_location?.timestamp || "";
          case "max_speed":
            return bike.max_speed ?? -1;
          case "created_at":
            return bike.created_at;
          case "bike_sn":
            return bike.bike_sn || "";
          case "status":
            return bike.status;
          case "bike_id":
          default:
            return bike.bike_id;
        }
      };

      const aVal = getSortValue(a);
      const bVal = getSortValue(b);
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [
    bikes,
    debouncedSearch,
    statusFilter,
    categoryFilter,
    cityFilter,
    sortBy,
    sortOrder,
  ]);

  const totalFiltered = filteredBikes.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const paginatedBikes = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredBikes.slice(start, start + itemsPerPage);
  }, [filteredBikes, currentPage, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleRefresh = () => {
    Promise.all([
      refreshBikes(
        showIncludeHidden ? { include_hidden: true } : undefined,
      ),
      refreshCategories(),
      refreshCities(),
    ]).catch(console.error);
  };

  const handleShowHiddenChange = (value: boolean) => {
    setShowIncludeHidden(value);
    refreshBikes(value ? { include_hidden: true } : undefined).catch(
      console.error,
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCreateBike = () => {
    setSelectedBike(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const handleEditBike = (bike: BikeWithLocation) => {
    setSelectedBike(bike);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleDeleteBike = (bike: BikeWithLocation) => {
    setSelectedBike(bike);
    setModalMode("delete");
    setModalOpen(true);
  };

  const handleModalSave = async (bikeData: Record<string, unknown>) => {
    try {
      if (modalMode === "create") {
        await addBike(bikeData as Omit<Bike, "created_at" | "updated_at">);
      } else if (modalMode === "edit" && selectedBike) {
        await updateBike(
          selectedBike.bike_id,
          bikeData as Partial<
            Pick<
              Bike,
              | "status"
              | "category_id"
              | "bike_sn"
              | "max_speed"
              | "comment"
              | "primary_city_id"
              | "ui_hidden"
            >
          >,
        );
      } else if (modalMode === "delete" && selectedBike) {
        await removeBike(selectedBike.bike_id);
      }
      await refreshBikes(
        showIncludeHidden ? { include_hidden: true } : undefined,
      );
      setModalOpen(false);
      setSelectedBike(null);
    } catch (error) {
      console.error("Error saving bike:", error);
      // Re-throw the error so the modal can handle it
      throw error;
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedBike(null);
  };

  const handleExportCsv = () => {
    const categoryNameById = new Map(
      categories.map((category) => [category.category_id, category.category_name]),
    );
    const cityNameById = new Map(
      cities.map((city) => [city.city_id, city.city_name]),
    );

    const csv = bikesToCsv(filteredBikes, {
      categoryNameById,
      cityNameById,
    });
    downloadAsCsv(csv, getVehicleCsvFilename().replace(/\.csv$/, ""));
  };

  const handleImportComplete = () => {
    refreshBikes(
      showIncludeHidden ? { include_hidden: true } : undefined,
    ).catch(console.error);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Vehicle Management
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {totalFiltered} vehicles total
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExportCsv}
            disabled={filteredBikes.length === 0}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
          <button
            onClick={() => setImportModalOpen(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
          <button
            onClick={handleCreateBike}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Vehicle
          </button>
        </div>
      </div>

      {/* Filters */}
      <BikeFilters
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        categoryFilter={categoryFilter}
        onCategoryFilterChange={setCategoryFilter}
        availableCategories={categories}
        cityFilter={cityFilter}
        onCityFilterChange={setCityFilter}
        availableCities={cities}
        searching={search !== debouncedSearch}
        showHiddenVehicles={showIncludeHidden}
        onShowHiddenVehiclesChange={handleShowHiddenChange}
      />

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading && bikes.length === 0 ? (
          <div className="flex items-center justify-center p-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            <BikeTable
              bikes={paginatedBikes}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onEdit={handleEditBike}
              onDelete={handleDeleteBike}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <BikePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={totalFiltered}
                itemsPerPage={itemsPerPage}
              />
            )}
          </>
        )}

        {/* Empty state */}
        {!loading && filteredBikes.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">
              {bikes.length === 0
                ? "No vehicles registered yet"
                : "No vehicles match your current filters"}
            </div>
            {bikes.length === 0 && (
              <button
                onClick={handleCreateBike}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add your first vehicle
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      <BikeModal
        open={modalOpen}
        onClose={handleModalClose}
        mode={modalMode}
        bike={selectedBike}
        onSave={handleModalSave}
      />

      <BikeCsvImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={handleImportComplete}
      />
    </div>
  );
};
